import globals from "@lvt/globals";
import { configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { TypedUseSelectorHook, useDispatch as useDispatchBase, useSelector as useSelectorBase } from "react-redux";
import { combineReducers, StoreEnhancer } from "redux";

import { vmsSearchApi } from "@/api/elasticSearch/vmsSearchApi";
import { liveUnitMosaicApi } from "@/api/liveUnitMosaic/liveUnitMosaic";
import { requestedVideosApi } from "@/api/requestedVideos";
import { speakerTalkdownApi } from "@/api/speakerTalkdown/speakerTalkdownApi";
import { vmsBaseApi } from "@/api/vmsBase";
import { elasticSearchReducer } from "@/store/elasticSearch/slice";
import { archiveCabinetsReducer as mapArchiveCabinetsReducer } from "@/store/map/archiveCabinets/slice";
import { liveUnitsReducer as mapLiveUnitsReducer } from "@/store/map/liveUnits/slice";

import { alertsReducer } from "./alerts/slice";
import { archivesReducer } from "./archives/slice";
import { camerasReducer } from "./cameras/slice";
import { clientsReducer } from "./clients/slice";
import { liveUnitNavReducer } from "./liveUnitNav/slice";
import { liveUnitsReducer } from "./liveUnits/slice";
import { mapReducer } from "./map/slice";
import { streamDataReducer } from "./streamData/slice";
import { userReducer } from "./user/slice";
import { userListsReducer } from "./userLists/slice";


const reducer = combineReducers({
    alerts: alertsReducer,
    archives: archivesReducer,
    cameras: camerasReducer,
    clients: clientsReducer,
    elasticSearch: elasticSearchReducer,
    liveUnitNav: liveUnitNavReducer,
    liveUnits: liveUnitsReducer,
    map: mapReducer,
    mapArchiveCabinets: mapArchiveCabinetsReducer,
    mapLiveUnits: mapLiveUnitsReducer,
    stream: streamDataReducer,
    user: userReducer,
    userLists: userListsReducer,
    [vmsSearchApi.reducerPath]: vmsSearchApi.reducer,
    [speakerTalkdownApi.reducerPath]: speakerTalkdownApi.reducer,
    [vmsBaseApi.reducerPath]: vmsBaseApi.reducer,
    [liveUnitMosaicApi.reducerPath]: liveUnitMosaicApi.reducer,
    [requestedVideosApi.reducerPath]: requestedVideosApi.reducer,
});

const enhancers: StoreEnhancer[] = [];

if (globals.sentry.dsn) {
    enhancers.push(Sentry.createReduxEnhancer());
}

export const createStore = (initialState: any = undefined) =>
    configureStore({
        reducer: reducer,
        enhancers: enhancers,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                immutableCheck: { ignoredPaths: ["api"] },
                serializableCheck: { ignoredPaths: ["api"] },
            })
                .concat(vmsSearchApi.middleware)
                .concat(speakerTalkdownApi.middleware)
                .concat(vmsBaseApi.middleware)
                .concat(liveUnitMosaicApi.middleware)
                .concat(requestedVideosApi.middleware),
        preloadedState: initialState,
    });

export type Store = ReturnType<typeof createStore>;
export type RootState = ReturnType<Store["getState"]>;

export const useDispatch = () => useDispatchBase<Store["dispatch"]>();
export type DispatchType = ReturnType<typeof useDispatch>;
export const useSelector: TypedUseSelectorHook<RootState> = useSelectorBase;
